"use client";

import { motion } from "framer-motion";

export interface Hero3BlockStyle {
    backgroundColor?: string;
    textColor?: string;
    descriptionColor?: string;
    metaColor?: string;
    tagBackgroundColor?: string;
    tagTextColor?: string;
    paddingTop?: string;
    paddingBottom?: string;

    // Image container styles
    imageBackgroundColor?: string;
    imageObjectFit?: string;
    imageObjectPosition?: string;
}

export interface Hero3BlockProps {
    title: string;
    description: string;
    image: string;
    metadata?: string;
    tags?: string[];
    style?: Hero3BlockStyle;
}

export default function Hero3Block({
    title,
    description,
    image,
    metadata,
    tags = [],
    style
}: Hero3BlockProps) {
    return (
        <div
            className="w-full"
            style={{
                backgroundColor: style?.backgroundColor || "#FFFFFF",
                paddingTop: style?.paddingTop || "6rem",
                paddingBottom: style?.paddingBottom || "6rem",
                color: style?.textColor || "#000000"
            }}
        >
            <div className="container mx-auto px-4 md:px-8 lg:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">

                    {/* Left Column: Image Container (Larger) */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="lg:col-span-7 xl:col-span-8 relative w-full aspect-[837/628] rounded-[32px] overflow-hidden flex items-center justify-center mb-8 lg:mb-0"
                        style={{ backgroundColor: style?.imageBackgroundColor || "transparent" }}
                    >
                        {/* Background effect/glow could go here */}
                        {image && (
                            <div className="relative w-full h-full overflow-hidden">
                                <img
                                    src={image}
                                    alt={title}
                                    className="w-full h-full"
                                    style={{
                                        objectFit: (style?.imageObjectFit as any) || "cover",
                                        objectPosition: style?.imageObjectPosition || "center"
                                    }}
                                />
                            </div>
                        )}
                    </motion.div>

                    {/* Right Column: Content (Compact) */}
                    <div className="lg:col-span-5 xl:col-span-4 flex flex-col justify-between h-full py-2">
                        {/* Top Content: Title & Description */}
                        <div>
                            <motion.h1
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight mb-8"
                            >
                                {title}
                            </motion.h1>

                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="text-lg md:text-xl leading-relaxed font-normal mb-8"
                                style={{ color: style?.descriptionColor || "#4B5563" }}
                                dangerouslySetInnerHTML={{ __html: description }}
                            />
                        </div>

                        {/* Bottom Content: Metadata & Tags */}
                        <div>
                            {metadata && (
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 0.4 }}
                                    className="text-sm uppercase tracking-wider font-medium mb-6"
                                    style={{ color: style?.metaColor || "#9CA3AF" }}
                                >
                                    {metadata}
                                </motion.p>
                            )}

                            {tags && tags.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.5 }}
                                    className="flex flex-wrap gap-3"
                                >
                                    {tags.map((tag, idx) => (
                                        <span
                                            key={idx}
                                            className="px-6 py-2 rounded-full text-xs font-medium border"
                                            style={{
                                                backgroundColor: style?.tagBackgroundColor || "#F3F4F6",
                                                color: style?.tagTextColor || "#1F2937",
                                                borderColor: "transparent"
                                            }}
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </motion.div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
